/**
 * Row Constraint Validator
 *
 * Validates that container operations comply with row constraints.
 * Primary rule: Maximum 3 containers per row.
 */

import type { ContainerAPI } from '../../types/api.types';
import type { ValidationResult, ValidationError } from '../types/container-row.types';
import { CONTAINER_CONSTRAINTS, type ContainerConstraints } from '../constants/constraints';
import { getRowContainerCount, canAddToRow } from '../utils/rowCalculations';

/**
 * Validates adding a container to a specific row
 *
 * @param containers - All containers in the canvas
 * @param targetRowIndex - Row index to add to
 * @param constraints - Container constraints (optional)
 * @returns Validation result
 */
export const validateRowConstraint = (
  containers: ContainerAPI[],
  targetRowIndex: number,
  constraints: ContainerConstraints = CONTAINER_CONSTRAINTS
): ValidationResult => {
  const errors: ValidationError[] = [];

  const currentCount = getRowContainerCount(containers, targetRowIndex);

  if (currentCount >= constraints.MAX_CONTAINERS_PER_ROW) {
    errors.push({
      code: 'ROW_FULL',
      message: `Row ${targetRowIndex} is full. Maximum ${constraints.MAX_CONTAINERS_PER_ROW} containers per row allowed.`,
      context: {
        rowIndex: targetRowIndex,
        currentCount,
        maxAllowed: constraints.MAX_CONTAINERS_PER_ROW,
      },
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: [],
  };
};

/**
 * Validates that a row can accept a new container
 *
 * @param containers - All containers in the canvas
 * @param rowIndex - Row index to check
 * @param constraints - Container constraints (optional)
 * @returns Whether the row can accept more containers
 */
export const validateCanAddToRow = (
  containers: ContainerAPI[],
  rowIndex: number,
  constraints: ContainerConstraints = CONTAINER_CONSTRAINTS
): boolean => {
  return canAddToRow(containers, rowIndex, constraints.MAX_CONTAINERS_PER_ROW);
};

/**
 * Gets the first available row that can accept a new container
 *
 * @param containers - All containers in the canvas
 * @param constraints - Container constraints (optional)
 * @returns Row index that can accept a new container, or -1 if all rows full
 */
export const findFirstAvailableRow = (
  containers: ContainerAPI[],
  constraints: ContainerConstraints = CONTAINER_CONSTRAINTS
): number => {
  if (containers.length === 0) {
    return 0;
  }

  // Get all unique row indices
  const rowIndices = Array.from(new Set(containers.map((c) => c.rowIndex ?? 0))).sort((a, b) => a - b);

  // Find first row with space
  for (const rowIndex of rowIndices) {
    if (validateCanAddToRow(containers, rowIndex, constraints)) {
      return rowIndex;
    }
  }

  // All existing rows full - return next row index
  const maxRowIndex = Math.max(...rowIndices);
  return maxRowIndex + 1;
};

/**
 * Validates that container count in row stays within limits after an operation
 *
 * @param containers - All containers after the proposed operation
 * @param rowIndex - Row to validate
 * @param constraints - Container constraints (optional)
 * @returns Validation result
 */
export const validateRowAfterOperation = (
  containers: ContainerAPI[],
  rowIndex: number,
  constraints: ContainerConstraints = CONTAINER_CONSTRAINTS
): ValidationResult => {
  const count = getRowContainerCount(containers, rowIndex);
  const errors: ValidationError[] = [];

  if (count > constraints.MAX_CONTAINERS_PER_ROW) {
    errors.push({
      code: 'ROW_FULL',
      message: `Operation would result in ${count} containers in row ${rowIndex}, exceeding maximum of ${constraints.MAX_CONTAINERS_PER_ROW}.`,
      context: {
        rowIndex,
        resultingCount: count,
        maxAllowed: constraints.MAX_CONTAINERS_PER_ROW,
      },
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: [],
  };
};
